import type { WorkingConditionDto } from '@customTypes/types';
import { ConsumationType } from '@customTypes/types';
import { useForm, zodResolver } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { notificationStyleConfig } from '@/features/notification/config/notificationConfig';
import { workingConditionsFormSchema } from '@/pages/club/onboard/components/stages/working-conditions/constants/schema';
import {
  useClubProfileWorkConditionMutation,
  useClubProfileWorkConditionQuery
} from '@/shared/api/requests/club';

const initialDressList = [
  'Bodysuit',
  'Long dress',
  'Short dress',
  'Lingerie',
  'Strip outfit'
];

export const useWorkingConditionPage = () => {
  const { formatMessage } = useIntl();
  const { id: clubId } = useParams({ from: '/clubOwner/$id' });
  const navigate = useNavigate({ from: '/clubOwner/$id/onboard/$step' });
  const workingConditionsQuery = useClubProfileWorkConditionQuery({ clubId });
  const workingConditionsMutation = useClubProfileWorkConditionMutation();
  const queryClient = useQueryClient();
  const isFormInitialized = useRef(false);

  const form = useForm<WorkingConditionDto>({
    initialValues: {
      consummationType: ConsumationType.Active,
      choreographerPresence: false,
      photoshootMandatory: false,
      artistsPerShift: 0,
      numberOfStages: 0,
      numberOfPolesDynamic: 0,
      numberOfPolesStatic: 0,
      experienceRequired: false,
      artistTraining: false,
      dressCodes: [],
      additionalInformation: ''
    },
    validate: zodResolver(workingConditionsFormSchema)
  });

  const [dressList, setDressList] = useState(initialDressList);

  const next = () => {
    navigate({
      to: '/clubOwner/$id/onboard/$step',
      params: {
        step: 'housing'
      }
    });
  };

  useEffect(() => {
    if (!isFormInitialized.current && workingConditionsQuery.data?.data) {
      form.initialize(workingConditionsQuery.data.data);
      setDressList([
        ...initialDressList.filter(
          (item) => !workingConditionsQuery.data.data.dressCodes.includes(item)
        ),
        ...workingConditionsQuery.data.data.dressCodes
      ]);
      isFormInitialized.current = true;
    }
  }, [workingConditionsQuery.data?.data]);

  const onSubmit = form.onSubmit(async (values) => {
    await workingConditionsMutation.mutateAsync(
      {
        params: {
          clubId,
          data: values
        }
      },
      {
        onSuccess: async () => {
          notifications.show({
            title: formatMessage({ id: 'notifications.successDataUpdate' }),
            ...notificationStyleConfig
          });
          await queryClient.invalidateQueries({
            queryKey: ['ClubProfileWorkCondition', clubId]
          });
          next();
        }
      }
    );
  });

  const queries = [workingConditionsQuery];

  const isLoading = queries.some((q) => q.isLoading);

  return {
    state: { isLoading, form, dressList },
    functions: { onSubmit, setDressList }
  };
};
